import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { BotaoContorno } from "../components/ui/BotaoAcao";
import { BotaoTopoPrimario } from "../components/ui/BotoesTopo";
import { useAuth } from "../auth/AuthContext";
import { useIdioma } from "../i18n/IdiomaProvider";
import { obterAceiteTermos, registrarAceiteTermos } from "../api/sistema";
import styles from "./AceiteTermosModal.module.css";

/**
 * Aceite dos termos exigido no primeiro acesso de cada login (LGPD).
 *
 * Não tem botão de fechar: ou o usuário aceita, ou é deslogado — usar a plataforma
 * sem aceite registrado é justamente o que a exigência evita. Documentos revisados
 * (nova versão em Configurações) fazem o modal voltar a aparecer para todos.
 */
export function AceiteTermosModal() {
  const { traduzir } = useIdioma();
  const { usuario, sair } = useAuth();
  const [aberto, setAberto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");

  useEffect(() => {
    if (!usuario) {
      setAberto(false);
      return;
    }
    let vivo = true;
    void obterAceiteTermos()
      .then((dados) => {
        if (vivo && dados?.sucesso !== false) setAberto(Boolean(dados?.precisaAceitar));
      })
      .catch(() => {
        /* Sem resposta do servidor não bloqueamos o acesso: o aceite é pedido de novo
           no próximo carregamento. */
      });
    return () => {
      vivo = false;
    };
  }, [usuario]);

  async function aceitar() {
    setSalvando(true);
    setErro("");
    try {
      const resposta = await registrarAceiteTermos();
      if (resposta?.sucesso === false) {
        setErro(traduzir("Não foi possível registrar seu aceite. Tente novamente."));
        return;
      }
      setAberto(false);
    } catch {
      setErro(traduzir("Não foi possível registrar seu aceite. Tente novamente."));
    } finally {
      setSalvando(false);
    }
  }

  async function rejeitar() {
    setSalvando(true);
    try {
      await sair();
    } finally {
      setSalvando(false);
    }
  }

  if (!aberto) return null;

  const documento = (rota: string, rotulo: string): ReactNode => (
    <Link to={rota} target="_blank" rel="noreferrer">
      {traduzir(rotulo)}
    </Link>
  );

  return (
    <div className={styles.overlay} role="presentation">
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="aceite-termos-titulo"
        data-vidro="true"
      >
        <h2 className={styles.titulo} id="aceite-termos-titulo">
          {traduzir("Termos e Condições de uso")}
        </h2>

        <div className={styles.texto}>
          <p>
            {traduzir(
              "Para reforçar o compromisso com nossos usuários e garantir o controle com relação ao uso de dados pessoais, em decorrência da Lei Geral de Proteção de Dados (nº 13.709/2018), estamos disponibilizando os Termos e Políticas de Privacidade para os usuários da nossa plataforma."
            )}
          </p>
          <p>
            {traduzir(
              "Para garantir a continuidade do uso de nossos serviços, por favor, revise essas informações e confirme estar de acordo com nossos termos."
            )}
          </p>
          <p>
            {traduzir("Ao clicar em")} <strong>{traduzir("Aceitar")}</strong>{" "}
            {traduzir("você concorda com os")} {documento("/termos", "Termos de Uso")},{" "}
            {documento("/politica", "Política de Privacidade")},{" "}
            {documento("/cookies", "Política de Cookies")} {traduzir("e")}{" "}
            {documento("/cancelamento", "Política de Cancelamento")}.
          </p>
        </div>

        {erro && <p className={styles.erro}>{erro}</p>}

        <div className={styles.acoes}>
          <BotaoContorno disabled={salvando} onClick={() => void rejeitar()}>
            {traduzir("Rejeitar")}
          </BotaoContorno>
          <BotaoTopoPrimario carregando={salvando} onClick={() => void aceitar()}>
            {traduzir("Aceitar")}
          </BotaoTopoPrimario>
        </div>
      </div>
    </div>
  );
}
